import { createContext, useContext, useState } from 'react'

interface AvatarOverrideContextValue {
  /** When set, overrides currentUser.avatar_url across the app */
  avatarOverride: string | null
  setAvatarOverride: (url: string | null) => void
}

const AvatarOverrideContext = createContext<AvatarOverrideContextValue>({
  avatarOverride: null,
  setAvatarOverride: () => {},
})

const HasAvatarOverrideProviderContext = createContext(false)

/**
 * Lightweight context that lets the profile page override the avatar URL
 * across the app without a full page reload. Set to '' to clear avatar,
 * or null to use the default from currentUser.
 *
 * Reuses an ancestor provider when one exists (the Arc shell mounts one
 * around the persistent top bar) so page-level providers don't shadow it —
 * the profile page setter must reach the shell's avatar. Standalone usage
 * (legacy pages, no ancestor) is unchanged.
 */
export function AvatarOverrideProvider({ children }: { children: React.ReactNode }) {
  const alreadyProvided = useContext(HasAvatarOverrideProviderContext)
  const [avatarOverride, setAvatarOverride] = useState<string | null>(null)

  if (alreadyProvided) return <>{children}</>

  return (
    <HasAvatarOverrideProviderContext.Provider value={true}>
      <AvatarOverrideContext.Provider value={{ avatarOverride, setAvatarOverride }}>
        {children}
      </AvatarOverrideContext.Provider>
    </HasAvatarOverrideProviderContext.Provider>
  )
}

export function useAvatarOverride() {
  return useContext(AvatarOverrideContext)
}
